// src/pages/pharmacy/PharmacyDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingCart, TrendingUp, AlertCircle, Plus, MapPin} from 'lucide-react';
import apiService from '../../services/api';

const PharmacyDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch stats
      const statsResponse = await apiService.get('/pharmacies/stats');
      if (statsResponse.success) {
        setStats(statsResponse.data);
      }

      // Fetch recent orders
      const ordersResponse = await apiService.get('/pharmacies/orders?limit=5');
      if (ordersResponse.success) {
        setRecentOrders(ordersResponse.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <p className={`text-2xl font-bold ${color} mt-1`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`${color} bg-opacity-10 p-3 rounded-lg`}>
          <Icon className={`w-8 h-8 ${color}`} />
        </div>
      </div>
    </div>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={fetchDashboardData}
          className="mt-2 text-red-600 hover:text-red-700 font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pharmacy Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage your pharmacy operations</p>
        </div>
        <Link
          to="/pharmacy/inventory"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </Link>
      </div>

      {/* Free Plan Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900">Free Plan</h3>
            <p className="text-blue-700 text-sm mt-1">
              You can post up to 3 products with 1 image each. Orders appear as bookings for you to contact patients.
            </p>
            <p className="text-blue-600 text-sm mt-1">
              Products Used: <span className="font-bold">{stats?.totalProducts || 0}/3</span>
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Package}
          title="Total Products"
          value={stats?.totalProducts || 0}
          color="text-blue-600"
          subtitle={`${stats?.productLimit - (stats?.totalProducts || 0)} slots remaining`}
        />
        <StatCard
          icon={ShoppingCart}
          title="Pending Orders"
          value={stats?.pendingOrders || 0}
          color="text-yellow-600"
          subtitle="Awaiting confirmation"
        />
        <StatCard
          icon={TrendingUp}
          title="Completed Orders"
          value={stats?.completedOrders || 0}
          color="text-green-600"
          subtitle="All time"
        />
        <StatCard
          icon={TrendingUp}
          title="Total Revenue"
          value={`KES ${(stats?.totalRevenue || 0).toLocaleString()}`}
          color="text-purple-600"
          subtitle="From completed orders"
        />
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Recent Orders</h2>
            <Link
              to="/pharmacy/orders"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View All
            </Link>
          </div>
        </div>

        <div className="overflow-x-auto">
          {recentOrders.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <ShoppingCart className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p>No orders yet</p>
              <p className="text-sm mt-1">Orders will appear here when patients place them</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentOrders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/pharmacy/orders/${order._id}`}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {order.orderNumber}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{order.customer?.name}</p>
                        <p className="text-sm text-gray-500">{order.customer?.phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900">
                        {order.items?.length} item(s)
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="font-medium text-gray-900">
                        KES {order.totals?.total.toLocaleString()}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/pharmacy/inventory"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
        >
          <Package className="w-10 h-10 text-blue-600 mb-3" />
          <h3 className="font-semibold text-gray-800 mb-2">Manage Inventory</h3>
          <p className="text-sm text-gray-600">Add, edit, or remove products from your inventory</p>
        </Link>

        <Link
          to="/pharmacy/orders"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
        >
          <ShoppingCart className="w-10 h-10 text-yellow-600 mb-3" />
          <h3 className="font-semibold text-gray-800 mb-2">View Orders</h3>
          <p className="text-sm text-gray-600">Manage customer orders and update their status</p>
        </Link>

        <Link
          to="/pharmacy/profile"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
        >
          <MapPin className="w-10 h-10 text-green-600 mb-3" />
          <h3 className="font-semibold text-gray-800 mb-2">Update Profile</h3>
          <p className="text-sm text-gray-600">Edit your pharmacy details and location</p>
        </Link>
      </div>
    </div>
  );
};

export default PharmacyDashboard;